import { Link, useNavigate } from "react-router-dom"
import { useEffect, useMemo, useRef, useState } from "react"
import { useUserRole } from "../UserRoleContext"
import { useOrganization } from "../OrganizationContext"
import { useUser } from "../UserContext"
import { PRIMARY_NAV_ITEMS } from "../constants/navigation"
import { supabase } from "../supabaseClient"

export default function NavBar() {
  const role = useUserRole()
  const user = useUser()
  const { organizations, selectedOrgId, setSelectedOrgId } = useOrganization()
  const selectedOrg = organizations.find((o) => o.id === selectedOrgId)
  const plan = useMemo(() => selectedOrg?.plan || {}, [selectedOrg?.plan])
  console.log("NavBar render", { selectedOrgId, plan })
  const navigate = useNavigate()

  const handleOrgChange = (e) => {
    const value = e.target.value
    console.log("Organization changed", value)
    setSelectedOrgId(value === "" ? null : value)
    navigate("/dashboard")
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate("/")
  }

  const [openMenu, setOpenMenu] = useState(null)
  const navRef = useRef(null)
  const menuRefs = useRef({})
  const toggleRefs = useRef({})

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!navRef.current || navRef.current.contains(event.target)) return
      setOpenMenu(null)
    }

    if (typeof document !== "undefined") {
      document.addEventListener("mousedown", handleDocumentClick)
      document.addEventListener("touchstart", handleDocumentClick)
    }

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("mousedown", handleDocumentClick)
        document.removeEventListener("touchstart", handleDocumentClick)
      }
    }
  }, [])

  const navLinks = useMemo(() => {
    const evaluate = (item) => {
      if (item.roles && !item.roles.includes(role)) return null
      if (item.featureFlag && role !== "admin" && !item.featureFlag(plan)) return null

      const resolvedItem = {
        ...item,
        to:
          typeof item.resolveTo === "function"
            ? item.resolveTo({ selectedOrgId })
            : item.to,
      }

      if (item.children) {
        const resolvedChildren = item.children
          .map(evaluate)
          .filter(Boolean)

        if (resolvedChildren.length === 0) {
          if (!resolvedItem.to) return null
          const { children, ...rest } = resolvedItem
          return rest
        }

        return {
          ...resolvedItem,
          children: resolvedChildren,
        }
      }

      return resolvedItem
    }

    return PRIMARY_NAV_ITEMS.map(evaluate).filter(Boolean)
  }, [plan, role, selectedOrgId])

  const focusFirstChild = (label) => {
    const menu = menuRefs.current[label]
    if (!menu) return
    const firstFocusable = menu.querySelector("a, button")
    if (!firstFocusable) return
    const focusElement = () => firstFocusable.focus()
    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(focusElement)
    } else {
      focusElement()
    }
  }

  const handleToggle = (label) => {
    setOpenMenu((prev) => (prev === label ? null : label))
  }

  const handleToggleKeyDown = (event, label) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setOpenMenu(label)
      focusFirstChild(label)
    } else if (event.key === "Escape") {
      setOpenMenu(null)
    }
  }

  const handleMenuKeyDown = (event, label) => {
    if (event.key === "Escape") {
      event.preventDefault()
      setOpenMenu(null)
      const toggle = toggleRefs.current[label]
      toggle?.focus()
    }
  }

  const handleDropdownBlur = (event, label) => {
    if (event.currentTarget.contains(event.relatedTarget)) return
    setOpenMenu((prev) => (prev === label ? null : prev))
  }

  const userInitials = useMemo(() => {
    if (!user) return "?"
    const metadata = user.user_metadata || user.raw_user_meta_data || {}
    const firstName =
      metadata.first_name ||
      metadata.firstName ||
      metadata.given_name ||
      (metadata.full_name || metadata.name || "").split(" ")[0]
    const lastName =
      metadata.last_name ||
      metadata.lastName ||
      metadata.family_name ||
      (() => {
        const parts = (metadata.full_name || metadata.name || "").trim().split(/\s+/)
        return parts.length > 1 ? parts[parts.length - 1] : ""
      })()

    const firstInitial = (firstName || "").trim().charAt(0)
    const lastInitial = (lastName || "").trim().charAt(0)

    if (firstInitial && lastInitial) {
      return `${firstInitial}${lastInitial}`.toUpperCase()
    }

    if (firstInitial) {
      return firstInitial.toUpperCase()
    }

    const fallback = (user.email || "").trim()
    if (fallback) {
      const letters = fallback.match(/[a-z]/gi)
      if (letters && letters[0]) return letters[0].toUpperCase()
    }

    return "?"
  }, [user])

  return (
    <nav className="nav-bar" ref={navRef}>
      <div className="nav-links">
        {navLinks.map((item) => {
          const menuId = `nav-menu-${item.label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")}`

          if (item.children && item.children.length > 0) {
            const isOpen = openMenu === item.label
            return (
              <div
                key={item.label}
                className={`nav-dropdown${isOpen ? " nav-dropdown--open" : ""}`}
                onBlur={(event) => handleDropdownBlur(event, item.label)}
              >
                <button
                  type="button"
                  className="nav-dropdown__toggle"
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                  aria-controls={menuId}
                  id={`${menuId}-toggle`}
                  onClick={() => handleToggle(item.label)}
                  onKeyDown={(event) => handleToggleKeyDown(event, item.label)}
                  ref={(node) => {
                    if (node) {
                      toggleRefs.current[item.label] = node
                    } else {
                      delete toggleRefs.current[item.label]
                    }
                  }}
                >
                  <span className="nav-dropdown__label">{item.label}</span>
                  <span className="nav-dropdown__icon" aria-hidden="true">
                    ▾
                  </span>
                </button>
                <div
                  className="nav-dropdown__menu"
                  role="menu"
                  id={menuId}
                  aria-labelledby={`${menuId}-toggle`}
                  data-open={isOpen ? "true" : "false"}
                  ref={(node) => {
                    if (node) {
                      menuRefs.current[item.label] = node
                    } else {
                      delete menuRefs.current[item.label]
                    }
                  }}
                  onKeyDown={(event) => handleMenuKeyDown(event, item.label)}
                >
                  {item.children.map((child) => (
                    <Link
                      key={`${item.label}-${child.label}`}
                      to={child.to}
                      role="menuitem"
                      className="nav-dropdown__item"
                      tabIndex={isOpen ? undefined : -1}
                      onClick={() => setOpenMenu(null)}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            )
          }

          return (
            <Link key={item.label} to={item.to} className="nav-link">
              {item.label}
            </Link>
          )
        })}
      </div>
      <div className="nav-actions">
        {organizations.length > 0 && (
          <select
            aria-label="Select organization"
            value={selectedOrgId ?? ""}
            onChange={handleOrgChange}
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
        <div className="user-menu">
          <Link
            to="/account"
            className="user-avatar"
            aria-label="Open account settings"
          >
            {userInitials}
          </Link>
          <div className="user-menu__dropdown" role="menu">
            <Link to="/account" className="user-menu__item" role="menuitem">
              Account Settings
            </Link>
            <button
              type="button"
              className="user-menu__item user-menu__item--action"
              onClick={handleLogout}
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </nav>
  )
}
